import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, ChevronRight, File as FileIcon, FileUp, Loader2, Lock } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

const STATUS_TONES = {
  new: { label: 'New', tone: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
  updated: { label: 'Updated', tone: 'bg-amber-500/15 text-amber-700 dark:text-amber-300' },
  unchanged: { label: 'Unchanged', tone: 'bg-muted text-muted-foreground' },
  deleted: { label: 'Deleted', tone: 'bg-rose-500/15 text-rose-700 dark:text-rose-300' },
};

const STATUS_ORDER = ['new', 'updated', 'deleted', 'unchanged'];

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
// Base64 grows a workbook by 4/3 and the server caps import bodies at 20 MB.
const MAX_IMPORT_BYTES = 15 * 1024 * 1024;

function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function capitalize(s) {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

function importFormatOf(file) {
  const name = String(file?.name || '').toLowerCase();
  if (name.endsWith('.xlsx') || file?.type === XLSX_MIME) return 'xlsx';
  if (/\.(md|markdown|txt)$/.test(name) || file?.type === 'text/markdown' || file?.type === 'text/plain') return 'md';
  return null;
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      resolve(dataUrl.slice(dataUrl.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error || new Error('read failed'));
    reader.readAsDataURL(file);
  });
}

// Where an issue points: a markdown line, or a workbook sheet and row.
function issueLocation(issue) {
  if (issue.sheet) return issue.row ? `${issue.sheet}, row ${issue.row}` : issue.sheet;
  return issue.line ? `line ${issue.line}` : '';
}

/**
 * Import dialog shared by the Knowledge Base and the Trigger Base: takes an Excel workbook (.xlsx)
 * or a Markdown file, shows the diff the server computes, and applies it once confirmed.
 *
 * `previewImport` / `applyImport` receive `{ markdown } | { xlsx: base64 }` plus `mode`. `copy`
 * carries the collection's wording: `description`, `xlsxHint`, `mdHint`, `itemNouns` ([singular,
 * plural]) and `fields` (labels for section/question/aliases/answer, plus `lockAfterAnswer` when
 * items carry that flag). `format` ('xlsx' | 'md') picks the copy until a file is chosen; after that
 * the file's own type wins, so either kind can be dropped whichever menu entry opened the dialog.
 */
function ItemsImportDialog({ open, format = 'md', onOpenChange, onApplied, previewImport, applyImport, copy }) {
  const [file, setFile] = useState(null);
  const [fileFormat, setFileFormat] = useState(null);
  // { markdown } or { xlsx: base64 } — spread straight into the preview/apply requests.
  const [payload, setPayload] = useState(null);
  const [mode, setMode] = useState('replace');
  const [preview, setPreview] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [error, setError] = useState(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    if (!open) {
      setFile(null);
      setFileFormat(null);
      setPayload(null);
      setPreview(null);
      setError(null);
      setMode('replace');
      setStatusFilter('all');
    }
  }, [open]);

  // Re-preview when the mode toggles so the deletion counts update.
  useEffect(() => {
    if (!payload?.markdown && !payload?.xlsx) return;
    let cancelled = false;
    setIsPreviewing(true);
    setError(null);
    previewImport({ ...payload, mode })
      .then((result) => {
        if (!cancelled) setPreview(result);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Preview failed');
      })
      .finally(() => {
        if (!cancelled) setIsPreviewing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [payload, mode, previewImport]);

  const handleFile = async (next) => {
    if (!next) return;
    const nextFormat = importFormatOf(next);
    if (!nextFormat) {
      toast.error('Unsupported file — choose an Excel workbook (.xlsx) or a Markdown file (.md).');
      return;
    }
    if (next.size > MAX_IMPORT_BYTES) {
      toast.error(`${next.name} is ${formatBytes(next.size)} — import files must be under 15 MB.`);
      return;
    }
    try {
      const nextPayload = nextFormat === 'xlsx' ? { xlsx: await readFileAsBase64(next) } : { markdown: await next.text() };
      setFile(next);
      setFileFormat(nextFormat);
      setPayload(nextPayload);
    } catch (err) {
      toast.error(`Could not read file: ${err?.message || err}`);
    }
  };

  const handleFileInput = (event) => {
    const next = event.target.files?.[0];
    if (next) handleFile(next);
    // Reset so the same filename can be re-picked.
    event.target.value = '';
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDraggingOver(false);
    const dropped = event.dataTransfer.files?.[0];
    if (dropped) handleFile(dropped);
  };

  const handleApply = async () => {
    if (!payload || !preview) return;
    setIsApplying(true);
    setError(null);
    try {
      const result = await applyImport({ ...payload, mode });
      const a = result?.applied || {};
      const parts = [];
      if (a.created) parts.push(`${a.created} created`);
      if (a.updated) parts.push(`${a.updated} updated`);
      if (a.deleted) parts.push(`${a.deleted} deleted`);
      if (a.unchanged) parts.push(`${a.unchanged} unchanged`);
      toast.success(`Import applied — ${parts.join(' · ') || 'no changes'}`);
      onApplied?.();
    } catch (err) {
      setError(err?.message || 'Apply failed');
    } finally {
      setIsApplying(false);
    }
  };

  const summary = preview?.summary;
  const items = Array.isArray(preview?.items) ? preview.items : [];
  const issues = Array.isArray(preview?.issues) ? preview.issues : [];
  const visibleItems = useMemo(() => {
    if (statusFilter === 'all') return items;
    return items.filter((it) => it.status === statusFilter);
  }, [items, statusFilter]);

  const manualDeletedCount = summary?.manualDeletedCount || 0;
  const [itemNoun, itemsNoun] = copy.itemNouns;
  const formatCopy =
    (fileFormat || format) === 'xlsx'
      ? {
          title: 'Import from Excel',
          drop: 'Drop an .xlsx workbook here or click to choose',
          hint: `${copy.xlsxHint} Markdown files work too.`,
        }
      : {
          title: 'Import from Markdown',
          drop: 'Drop a .md file here or click to choose',
          hint: `${copy.mdHint} Excel workbooks work too.`,
        };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileUp className="h-5 w-5" />
            {formatCopy.title}
          </DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>

        <ScrollArea className="min-h-0 flex-1" viewportClassName="pr-1">
          <div className="space-y-5">
          {!file && (
            <label
              onDragOver={(e) => {
                e.preventDefault();
                setIsDraggingOver(true);
              }}
              onDragLeave={() => setIsDraggingOver(false)}
              onDrop={handleDrop}
              className={cn(
                'flex cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-border bg-muted/20 py-12 text-center transition-colors',
                isDraggingOver && 'border-primary bg-primary/5',
              )}
            >
              <FileUp className="h-10 w-10 text-muted-foreground" />
              <div className="space-y-1">
                <p className="text-sm font-medium">{formatCopy.drop}</p>
                <p className="text-xs text-muted-foreground">{formatCopy.hint}</p>
              </div>
              <input
                type="file"
                accept={`.xlsx,${XLSX_MIME},.md,text/markdown,text/plain`}
                className="hidden"
                onChange={handleFileInput}
              />
            </label>
          )}

          {file && (
            <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/30 p-3">
              <div className="flex min-w-0 items-center gap-3">
                <FileIcon className="h-5 w-5 shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{file.name}</p>
                  <p className="text-xs text-muted-foreground">{formatBytes(file.size)}</p>
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFile(null);
                  setFileFormat(null);
                  setPayload(null);
                  setPreview(null);
                  setError(null);
                  setStatusFilter('all');
                }}
              >
                Replace file
              </Button>
            </div>
          )}

          {file && (
            <div className="space-y-2 rounded-md border border-border p-3">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Mode</p>
              <div className="flex flex-col gap-2">
                <ModeRadio
                  current={mode}
                  value="replace"
                  onSelect={setMode}
                  title="Replace all"
                  description={`Drops the whole Mongo + Qdrant snapshot and rebuilds from the file. ${capitalize(itemsNoun)} not in this file are deleted.`}
                  destructive
                />
                <ModeRadio
                  current={mode}
                  value="merge"
                  onSelect={setMode}
                  title="Merge"
                  description={`Upserts by sourceId. Existing ${itemsNoun} without a sourceId (UI-only rows) are left alone.`}
                />
              </div>
              {mode === 'replace' && manualDeletedCount > 0 && (
                <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    Replace mode will delete <strong>{manualDeletedCount}</strong> hand-edited{' '}
                    {manualDeletedCount === 1 ? itemNoun : itemsNoun} without a sourceId. Switch to <em>Merge</em> to
                    preserve them.
                  </span>
                </div>
              )}
            </div>
          )}

          {isPreviewing && (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Computing diff…
            </div>
          )}

          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          {preview && summary && !isPreviewing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <SummaryTile label="New" value={summary.new} tone="emerald" />
                <SummaryTile label="Updated" value={summary.updated} tone="amber" />
                <SummaryTile label="Unchanged" value={summary.unchanged} tone="muted" />
                <SummaryTile label="Deleted" value={summary.deleted} tone="rose" />
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <SummaryTile label="Parsed entries" value={summary.totalParsed} tone="muted" />
                <SummaryTile label="Issues" value={summary.errors} tone={summary.errors ? 'rose' : 'muted'} />
                <SummaryTile
                  label="Manual rows"
                  value={mode === 'replace' ? `${summary.manualKeptCount} kept · ${summary.manualDeletedCount} dropped` : summary.manualKeptCount}
                  tone="muted"
                />
              </div>

              {issues.length > 0 && (
                <div className="space-y-2 rounded-md border border-destructive/30 bg-destructive/5 p-3">
                  <p className="text-xs font-semibold text-destructive">Issues</p>
                  <ul className="space-y-1 text-xs">
                    {issues.map((issue, idx) => {
                      const location = issueLocation(issue);
                      // Workbook issues can point at a sheet or row alone; markdown ones always name an id.
                      const showId = Boolean(issue.sourceId) || !issue.sheet;
                      return (
                        <li key={`${issue.sourceId || issue.sheet || 'issue'}-${idx}`}>
                          {showId ? <span className="font-mono text-[11px]">{issue.sourceId || '?'}</span> : null}
                          {location ? (
                            <span className="text-muted-foreground">
                              {showId ? ' · ' : ''}
                              {location}
                            </span>
                          ) : null}
                          <span className="text-muted-foreground"> — </span>
                          <span>{issue.message}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Filter</p>
                {['all', ...STATUS_ORDER].map((key) => {
                  const label = key === 'all' ? `All (${items.length})` : `${STATUS_TONES[key]?.label} (${summary[key] || 0})`;
                  const isActive = statusFilter === key;
                  return (
                    <Button
                      key={key}
                      type="button"
                      variant={isActive ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setStatusFilter(key)}
                      className="h-7"
                    >
                      {label}
                    </Button>
                  );
                })}
              </div>

              <ScrollArea className="max-h-[40vh] rounded-md border border-border">
                <ul className="divide-y divide-border">
                  {visibleItems.map((entry) => (
                    <ImportRow key={`${entry.sourceId}-${entry.status}`} entry={entry} copy={copy} />
                  ))}
                  {visibleItems.length === 0 && (
                    <li className="px-4 py-6 text-center text-sm text-muted-foreground">
                      No rows in this slice.
                    </li>
                  )}
                </ul>
              </ScrollArea>
            </div>
          )}
          </div>
        </ScrollArea>

        <DialogFooter className="border-t border-border pt-3">
          <Button type="button" variant="ghost" onClick={() => onOpenChange?.(false)} disabled={isApplying}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleApply}
            disabled={!preview || isApplying || isPreviewing || summary?.totalParsed === 0}
            variant={mode === 'replace' ? 'destructive' : 'default'}
          >
            {isApplying && <Spinner className="mr-2 h-4 w-4" />}
            {mode === 'replace' ? 'Replace all' : 'Apply merge'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportRow({ entry, copy }) {
  const meta = STATUS_TONES[entry.status] || STATUS_TONES.unchanged;
  const { fields } = copy;
  const [expanded, setExpanded] = useState(false);
  const canExpand = entry.status === 'updated' && Array.isArray(entry.changed) && entry.changed.length > 0;
  // Rows the file left without an ID (see assignMissingSourceIDs on the server).
  const idOrigin = {
    generated: { label: 'new ID', title: 'No ID in the file — assigned the next free one' },
    matched: {
      label: 'ID matched',
      title: `No ID in the file — matched the existing ${copy.itemNouns[0]} with the same ${fields.question.toLowerCase()}`,
    },
  }[entry.idOrigin];

  return (
    <li className="px-4 py-3 text-sm">
      <button
        type="button"
        className={cn('flex w-full items-start gap-3 text-left', canExpand ? 'cursor-pointer' : 'cursor-default')}
        onClick={() => canExpand && setExpanded((v) => !v)}
      >
        <span className={cn('mt-0.5 inline-flex shrink-0 items-center rounded px-2 py-0.5 text-[10px] font-semibold', meta.tone)}>
          {meta.label}
        </span>
        <div className="min-w-0 flex-1 space-y-0.5">
          <p className="truncate font-medium">
            <span className="font-mono text-xs text-muted-foreground">{entry.sourceId}</span>
            {idOrigin ? (
              <span
                title={idOrigin.title}
                className="ml-1.5 rounded bg-sky-500/15 px-1 py-px align-middle text-[10px] font-semibold text-sky-700 dark:text-sky-300"
              >
                {idOrigin.label}
              </span>
            ) : null}
            {fields.lockAfterAnswer && entry.lockAfterAnswer ? (
              <Lock
                className="ml-1.5 inline h-3 w-3 align-[-1px] text-amber-600 dark:text-amber-400"
                aria-label={fields.lockAfterAnswer}
              />
            ) : null}
            {entry.question ? <> · {entry.question}</> : null}
          </p>
          <p className="line-clamp-1 text-xs text-muted-foreground">{entry.section || 'Uncategorised'}</p>
          {canExpand && (
            <p className="text-xs text-muted-foreground">
              Changed: {entry.changed.map((key) => (fields[key] || key).toLowerCase()).join(', ')}
            </p>
          )}
        </div>
        {canExpand && (
          <ChevronRight
            className={cn('mt-1 h-4 w-4 shrink-0 transition-transform', expanded && 'rotate-90')}
          />
        )}
      </button>
      {canExpand && expanded && (
        <div className="mt-3 grid gap-3 rounded-md border border-border bg-muted/30 p-3 text-xs md:grid-cols-2">
          <DiffPane title="Current" snapshot={entry.current} fields={fields} />
          <DiffPane title="Incoming" snapshot={entry} fields={fields} />
        </div>
      )}
    </li>
  );
}

function DiffPane({ title, snapshot, fields }) {
  if (!snapshot) return null;
  return (
    <div className="space-y-2">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
      <div className="space-y-1.5">
        <KeyValue label={fields.section} value={snapshot.section} />
        <KeyValue label={fields.question} value={snapshot.question} />
        <KeyValue label={fields.aliases} value={(snapshot.aliases || []).join(' | ')} />
        <KeyValue label={fields.answer} value={snapshot.answer} multiline />
        {fields.lockAfterAnswer && typeof snapshot.lockAfterAnswer === 'boolean' ? (
          <KeyValue label={fields.lockAfterAnswer} value={snapshot.lockAfterAnswer ? 'Yes' : 'No'} />
        ) : null}
      </div>
    </div>
  );
}

function KeyValue({ label, value, multiline }) {
  return (
    <div>
      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={cn('text-xs', multiline ? 'whitespace-pre-wrap' : 'truncate')}>{value || <span className="italic text-muted-foreground">—</span>}</p>
    </div>
  );
}

function SummaryTile({ label, value, tone }) {
  const toneClass = {
    emerald: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
    amber: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
    rose: 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
    muted: 'bg-muted text-foreground',
  }[tone] || 'bg-muted text-foreground';
  return (
    <div className={cn('rounded-md px-3 py-2', toneClass)}>
      <p className="text-[10px] font-semibold uppercase tracking-wide opacity-80">{label}</p>
      <p className="text-lg font-semibold">{value ?? 0}</p>
    </div>
  );
}

function ModeRadio({ current, value, onSelect, title, description, destructive }) {
  const isActive = current === value;
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      className={cn(
        'flex items-start gap-3 rounded-md border border-input px-3 py-2 text-left transition-colors hover:bg-muted/40',
        isActive && (destructive ? 'border-destructive bg-destructive/5' : 'border-primary bg-primary/5'),
      )}
    >
      <span
        className={cn(
          'mt-1 inline-block h-4 w-4 shrink-0 rounded-full border',
          isActive ? (destructive ? 'border-destructive bg-destructive' : 'border-primary bg-primary') : 'border-input',
        )}
      />
      <span className="space-y-1">
        <span className={cn('block text-sm font-semibold', destructive && isActive && 'text-destructive')}>{title}</span>
        <span className="block text-xs text-muted-foreground">{description}</span>
      </span>
    </button>
  );
}

export default ItemsImportDialog;
